import * as React from "react";
import { ChartDataPoint } from "../ChartDataPoint";
import { Alert, Box, CircularProgress } from "@mui/material";
import { LinearChart } from "../LinearChart/LinearChart";
import { ExportLinearChartButton } from "../LinearChart/ExportLinearChartButton";
import { IntervalSelector, intervalOptions } from "../IntervalSelector/IntervalSelector";
import { useQuery } from "react-query";
import { MetricsService } from "../../../gen/clients";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getDisabledIntervals = (from: Date, to: Date): string[] => {
  const diffMs = to.getTime() - from.getTime();
  const diffDays = diffMs / MS_PER_DAY;

  const disabled: string[] = [];

  // Disable 1 minute and 5 minutes if more than 1 day
  if (diffDays > 1) {
    disabled.push("1m", "5m");
  }

  // Disable 15 minutes if more than 7 days
  if (diffDays > 7) {
    disabled.push("15m");
  }

  // Disable 1 hour if more than 30 days
  if (diffDays > 30) {
    disabled.push("1h");
  }

  return disabled;
};

const getFirstEnabledInterval = (disabledIntervals: string[]): string => {
  const firstEnabled = intervalOptions.find(option => !disabledIntervals.includes(option.value));
  return firstEnabled?.value ?? "1d";
};

interface Props {
  id: string;
  title: string;
  legend: string;
  from: Date;
  to: Date;
  buildQuery: (interval: string) => Record<string, any>;
  parseResponse: (response: any) => ChartDataPoint[];
  refetchRef: React.MutableRefObject<() => void>;
}

const ConnectedLinearChart: React.FC<Props> = ({
  id,
  title,
  legend,
  from,
  to,
  buildQuery,
  parseResponse,
  refetchRef
}) => {
  const [interval, setInterval] = React.useState<string>("1d");

  const disabledIntervals = React.useMemo(() => getDisabledIntervals(from, to), [from, to]);

  // Auto-select first enabled interval if current interval becomes disabled
  React.useEffect(() => {
    if (disabledIntervals.includes(interval)) {
      setInterval(getFirstEnabledInterval(disabledIntervals));
    }
  }, [disabledIntervals, interval]);

  const fromISO = from.toISOString();
  const toISO = to.toISOString();

  const openSearchQuery = React.useMemo(() => buildQuery(interval), [buildQuery, interval]);

  const { data, isLoading, error, isFetching, refetch } = useQuery(
    ["openSearchQuery", id, fromISO, toISO, interval, openSearchQuery],
    () =>
      MetricsService.searchIndex({
        indexName: "app-events-v2",
        requestBody: openSearchQuery
      }),
    {
      keepPreviousData: true,
      cacheTime: 0
    }
  );

  refetchRef.current = refetch;

  const chartData = React.useMemo(() => {
    if (!data) {
      return [];
    }
    return parseResponse(data);
  }, [data, parseResponse]);

  const onIntervalChange = React.useCallback((newInterval: string) => {
    setInterval(newInterval);
  }, []);

  const dataIsLoading = isLoading || isFetching;

  const renderContent = () => {
    if (dataIsLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error">Error loading data: {error instanceof Error ? error.message : "Unknown error"}</Alert>
      );
    }
    if (chartData.length === 0) {
      return <Alert severity="info">No data found for the selected time range.</Alert>;
    }
    return undefined;
  };

  const content = renderContent();

  return (
    <Box sx={{ mt: 4 }}>
      <LinearChart
        title={title}
        data={!content ? chartData : undefined}
        headerActions={
          !content ? (
            <>
              <IntervalSelector interval={interval} onChange={onIntervalChange} disabledIntervals={disabledIntervals} />
              <ExportLinearChartButton data={chartData} from={from} to={to} title={title} />
            </>
          ) : undefined
        }
        legend={legend}
      >
        {content}
      </LinearChart>
    </Box>
  );
};

export { ConnectedLinearChart };
